/**
 * Shipping estimator for cart drawer and cart page.
 * Calculation still uses /cart/shipping_rates.json.
 * Each .est-calc instance is initialized independently so both can exist on the same page.
 */
(function () {
  'use strict';

  if (window.__estCalculatorLoaded) return;
  window.__estCalculatorLoaded = true;

  function initAllShippingEstimators() {
    document.querySelectorAll('.est-calc').forEach(initShippingEstimator);
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getShippingErrorMessage(data) {
    if (!data || typeof data !== 'object') return '';

    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error.trim();
    }

    var fieldLabels = {
      zip: 'ZIP code',
      country: 'Country',
      province: 'Province'
    };
    var prioritizedFields = ['zip', 'country', 'province'];

    for (var i = 0; i < prioritizedFields.length; i += 1) {
      var field = prioritizedFields[i];
      var value = data[field];
      if (Array.isArray(value) && value.length > 0) {
        return String(value[0]).trim();
      }
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    var keys = Object.keys(data);
    for (var j = 0; j < keys.length; j += 1) {
      var key = keys[j];
      var messages = data[key];
      if (Array.isArray(messages) && messages.length > 0) {
        var firstMessage = String(messages[0]).trim();
        if (!firstMessage) continue;
        var label = fieldLabels[key] || key.charAt(0).toUpperCase() + key.slice(1);
        return label + ': ' + firstMessage;
      }
      if (typeof messages === 'string' && messages.trim()) {
        var readableLabel = fieldLabels[key] || key.charAt(0).toUpperCase() + key.slice(1);
        return readableLabel + ': ' + messages.trim();
      }
    }

    return '';
  }

  function initShippingEstimator(root) {
    if (!root || root.dataset.estimatorBound === 'true') return;

    var countrySelect = root.querySelector('[data-country-select]');
    var provinceSelect = root.querySelector('[data-province-select]');
    var estimateBtn = root.querySelector('[data-calculate]');
    var zipInput = root.querySelector('[data-zip-input]');
    var resultsDiv = root.querySelector('[data-results]');
    var provinceRow = root.querySelector('[data-province-row]');
    var shippingEl = root.querySelector('[data-shipping-value]');
    var totalEl = root.querySelector('[data-total-value]');
    var subtotalEl = root.querySelector('[data-subtotal-value]');

    if (!countrySelect || !provinceSelect || !estimateBtn || !zipInput || !resultsDiv) {
      return;
    }

    root.dataset.estimatorBound = 'true';

    var provinceData = {};
    Array.prototype.forEach.call(countrySelect.options, function (opt) {
      var provinces = opt.getAttribute('data-provinces');
      if (provinces) {
        try {
          provinceData[opt.value] = JSON.parse(provinces);
        } catch (e) {
          provinceData[opt.value] = [];
        }
      } else {
        provinceData[opt.value] = [];
      }
    });

    function updateProvinces(selectedCountry, selectedProvince) {
      var provinces = provinceData[selectedCountry] || [];
      provinceSelect.innerHTML = '<option value="">Select Province</option>';

      if (provinces.length > 0) {
        provinceSelect.style.display = 'block';
        if (provinceRow) provinceRow.style.display = '';
        provinces.forEach(function (prov) {
          var option = document.createElement('option');
          option.value = prov[0];
          option.text = prov[1];
          provinceSelect.appendChild(option);
        });
      } else {
        provinceSelect.style.display = 'none';
        if (provinceRow) provinceRow.style.display = 'none';
      }

      if (selectedProvince) {
        Array.prototype.forEach.call(provinceSelect.options, function (opt) {
          if (
            (opt.value || '').toLowerCase().trim() === selectedProvince.toLowerCase().trim() ||
            (opt.text || '').toLowerCase().trim() === selectedProvince.toLowerCase().trim()
          ) {
            opt.selected = true;
          }
        });
      }
    }

    countrySelect.addEventListener('change', function () {
      updateProvinces(this.value, null);
      resultsDiv.innerHTML = '';
      updateEstimatedTotal(null);
    });

    var defaultButtonText = estimateBtn.textContent;

    function setCalculating(isCalculating) {
      estimateBtn.disabled = isCalculating;
      estimateBtn.setAttribute('aria-busy', isCalculating ? 'true' : 'false');
      estimateBtn.textContent = isCalculating ? 'Calculating...' : defaultButtonText;
    }

    function updateEstimatedTotal(rate) {
      if (!shippingEl || !totalEl) return;

      var subtotalCents = subtotalEl ? parseInt(subtotalEl.dataset.cents || '0', 10) : 0;

      if (!rate) {
        shippingEl.textContent = '—';
        totalEl.textContent = subtotalEl ? subtotalEl.textContent : '';
        return;
      }

      var shippingCents = Math.round(parseFloat(rate.price) * 100);
      var totalCents = subtotalCents + shippingCents;
      shippingEl.textContent = formatMoney(shippingCents, rate.currency);
      totalEl.textContent = formatMoney(totalCents, rate.currency);
    }

    function calculateShipping() {
      var country = countrySelect.value;
      var province = provinceSelect.value;
      var zip = zipInput.value;

      if (!country) {
        resultsDiv.innerHTML = '<p>Please select a country.</p>';
        return;
      }

      resultsDiv.innerHTML = '';
      updateEstimatedTotal(null);
      setCalculating(true);

      fetch(
        '/cart/shipping_rates.json?shipping_address[zip]=' +
          encodeURIComponent(zip) +
          '&shipping_address[country]=' +
          encodeURIComponent(country) +
          '&shipping_address[province]=' +
          encodeURIComponent(province)
      )
        .then(function (response) {
          return response.json().then(function (data) {
            return {
              ok: response.ok,
              data: data
            };
          });
        })
        .then(function (payload) {
          var data = payload.data;
          if (!payload.ok) {
            var message = getShippingErrorMessage(data) || 'Error fetching shipping rates. Please try again.';
            resultsDiv.innerHTML = '<p>' + escapeHtml(message) + '</p>';
            updateEstimatedTotal(null);
            return;
          }

          if (data.shipping_rates && data.shipping_rates.length > 0) {
            if (data.shipping_rates.length === 1) {
              resultsDiv.innerHTML = '';
              updateEstimatedTotal(data.shipping_rates[0]);
              return;
            }

            var html = '<label class="est-calc__label" for="' + root.id + '-rate">Shipping method</label>';
            html += '<div class="select"><select id="' + root.id + '-rate" class="select__select">';
            data.shipping_rates.forEach(function (rate, index) {
              html +=
                '<option value="' +
                index +
                '">' +
                rate.name +
                ' — ' +
                rate.currency +
                ' ' +
                rate.price +
                '</option>';
            });
            html += '</select></div>';
            resultsDiv.innerHTML = html;
            updateEstimatedTotal(data.shipping_rates[0]);

            var rateSelect = resultsDiv.querySelector('select');
            if (rateSelect) {
              rateSelect.addEventListener('change', function () {
                var selectedIndex = parseInt(rateSelect.value, 10);
                updateEstimatedTotal(data.shipping_rates[selectedIndex] || data.shipping_rates[0]);
              });
            }
          } else {
            var noRateMessage = getShippingErrorMessage(data) || 'No shipping rates available for this address.';
            resultsDiv.innerHTML = '<p>' + escapeHtml(noRateMessage) + '</p>';
            updateEstimatedTotal(null);
          }
        })
        .catch(function () {
          resultsDiv.innerHTML = '<p>Error fetching shipping rates. Please try again.</p>';
          updateEstimatedTotal(null);
        })
        .finally(function () {
          setCalculating(false);
        });
    }

    estimateBtn.addEventListener('click', calculateShipping);
    root._calculateShipping = calculateShipping;
    root._updateSubtotal = function (cents) {
      if (!subtotalEl || typeof cents !== 'number') return;
      subtotalEl.dataset.cents = String(cents);
      subtotalEl.textContent = formatMoney(cents);
    };

    var isLoggedIn = root.dataset.customerLoggedIn === 'true';
    var savedCountry = root.dataset.customerCountry;
    var savedProvince = root.dataset.customerProvince;
    var savedZip = root.dataset.customerZip;

    if (savedCountry) {
      Array.prototype.forEach.call(countrySelect.options, function (opt) {
        if (
          (opt.value || '').toLowerCase().trim() === savedCountry.toLowerCase().trim() ||
          (opt.text || '').toLowerCase().trim() === savedCountry.toLowerCase().trim()
        ) {
          countrySelect.value = opt.value;
        }
      });

      updateProvinces(countrySelect.value, savedProvince || null);
      zipInput.value = savedZip || '';
    }

    if (isLoggedIn && countrySelect.value) {
      calculateShipping();
    }
  }

  function formatMoney(cents, currency) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      var fmt = window.Shopify.money_format || window.Shopify.moneyFormat || '{{amount}}';
      return window.Shopify.formatMoney(cents, fmt);
    }
    var amount = (cents / 100).toFixed(2);
    return currency ? currency + ' ' + amount : amount;
  }

  function observeContainer(selector) {
    var el = document.querySelector(selector);
    if (!el) return;

    var observer = new MutationObserver(function () {
      clearTimeout(observer._timer);
      observer._timer = setTimeout(initAllShippingEstimators, 50);
    });

    observer.observe(el, { childList: true, subtree: true });
  }

  function bindCartUpdates() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var cart = event && event.cartData;
      if (!cart || typeof cart.total_price !== 'number') return;

      document.querySelectorAll('.est-calc').forEach(function (root) {
        if (typeof root._updateSubtotal === 'function') {
          root._updateSubtotal(cart.total_price);
        }
        if (typeof root._calculateShipping === 'function') {
          var countrySelect = root.querySelector('[data-country-select]');
          if (countrySelect && countrySelect.value) {
            root._calculateShipping();
          }
        }
      });
    });
  }

  function bootstrap() {
    initAllShippingEstimators();
    observeContainer('#CartDrawer');
    observeContainer('#main-cart-footer');
    bindCartUpdates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
